<template>
  <div class="payment-container">
    <div class="payment-card">

      <!-- Error -->
      <div
        v-if="error"
        class="status not-found"
      >
        {{ error }}
      </div>

      <template v-else>

        <!-- ================= WAITING ================= -->

        <div v-if="status === 'WAITING'">

          <div class="info">
            <div class="title">
              Invoice
            </div>
            <div class="value">
              {{ data.transaction_id }}
            </div>
          </div>
          <br />
          <div class="info">
            <div class="title">
              Total Amount
            </div>

            <div class="value">
              IDR {{ formatRupiah(data.amount) }}
            </div>
          </div>

          <p class="instruction">
            Scan the QR code below with your payment application.
          </p>

          <div class="qr-section">

            <img
              v-if="qrImage"
              :src="qrImage"
              class="qr-image"
              alt="QRIS"
              @error="handleImageError"
            >

          </div>

          <div class="status waiting">
            Waiting for Payment
          </div>

          <div class="info">
            <div class="title">
              Expired In
            </div>

            <div class="value">
              {{ countdown }}
            </div>
          </div>
          <br/>
          <button
            class="btn"
            :disabled="loading"
            @click="checkPayment(true)"
          >
            {{ loading ? "Checking..." : "Confirm Payment" }}
          </button>

        </div>

        <!-- ================= SUCCESS ================= -->

        <div
          v-else-if="status === 'PAID'"
          class="success-container"
        >

          <div class="success-icon">
            ✓
          </div>

          <h2 class="success-title">
            Payment Successful
          </h2>

          <p class="success-message">
            Thank you for your payment.
            <br>
            Your order will be processed shortly.
          </p>

          <div class="receipt">

            <div class="receipt-row">
              <span>Invoice</span>
              <strong>{{ data.transaction_id }}</strong>
            </div>

            <div class="receipt-row">
              <span>Amount</span>
              <strong>IDR {{ formatRupiah(data.amount) }}</strong>
            </div>

            <div class="receipt-row">
              <span>Paid At</span>
              <strong>{{ formatDate(data.paytime) }}</strong>
            </div>

          </div>

        </div>

        <!-- ================= EXPIRED ================= -->

        <div
          v-else
          class="expired-container"
        >

          <div class="expired-icon">
            ⏰
          </div>

          <h2 class="expired-title">
            QR Code Expired
          </h2>

          <p class="expired-message">
            This QRIS payment has expired.
            <br>
            Please create a new payment.
          </p>

          <div class="receipt">

            <div class="receipt-row">
              <span>Invoice</span>
              <strong>{{ data.transaction_id || "-" }}</strong>
            </div>

            <div class="receipt-row">
              <span>Amount</span>
              <strong>IDR {{ formatRupiah(data.amount) }}</strong>
            </div>

          </div>

        </div>

      </template>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      data: {},

      invoiceNo: "",

      status: "WAITING",

      countdown: "--:--",

      loading: false,

      error: "",

      polling: null,
      timer: null
    };
  },

  computed: {
    qrImage() {
      if (!this.invoiceNo) return "";

      return `${process.env.VUE_APP_API_ROOMI_URL}admin/payment/qris_image/${this.invoiceNo}`;
    }
  },

  async mounted() {
    const invoice =
      this.$route.params.invoice ||
      this.$route.query.invoice ||
      "";

    if (!invoice) {
      this.error = "Invoice not found";
      this.status = "NOT_FOUND";
      return;
    }

    this.invoiceNo = invoice.trim().toUpperCase();

    await this.checkPayment(false);

    if (this.status === "WAITING") {
      this.startPolling();
    }
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {

    //----------------------------------
    // PAYMENT
    //----------------------------------

    async checkPayment(showLoading = true) {

      if (showLoading && this.loading)
        return;

      if (showLoading)
        this.loading = true;

      if (this.status === "EXPIRED" && Number(payment.status) === 0) {
          return;
      }

      try {

        const { data } = await axios.get(
          `${process.env.VUE_APP_API_ROOMI_URL}admin/payment/detail`,
          {
            params: {
              invoice_id: this.invoiceNo
            },
            timeout: 10000
          }
        );

        const payment = data.data;

        if (!payment) {

          this.error = "Invoice not found";

          this.setStatus("NOT_FOUND");

          return;

        }

        this.data = payment;

        switch (Number(payment.status)) {

          case 0:

            this.setStatus("WAITING");

            if (!this.timer)
              this.startCountdown();

            break;

          case 1:

            this.setStatus("PAID");

            break;

          default:

            this.setStatus("EXPIRED");

        }

      } catch (err) {

        console.error(err);

        this.error =
          err.response?.data?.msg ||
          err.response?.data?.message ||
          "Unable to load payment.";

        this.setStatus("NOT_FOUND");

      } finally {

        this.loading = false;

      }

    },

    //----------------------------------
    // STATUS
    //----------------------------------

    // setStatus(status) {

    //   this.status = status;

    //   if (status !== "WAITING") {
    //     this.cleanup();
    //   }

    // },
    setStatus(status) {
      if (this.status === status)
          return;

      this.status = status;

      if (status === "PAID") {
          this.cleanup();
      }
    },

    //----------------------------------
    // POLLING
    //----------------------------------

    startPolling() {

      if (this.polling)
        return;

      this.polling = setInterval(() => {

        this.checkPayment(false);

      }, 3000);

    },


    stopPolling() {

      clearInterval(this.polling);

      this.polling = null;

    },

    //----------------------------------
    // COUNTDOWN
    //----------------------------------

    startCountdown() {

      if (this.timer)
        return;

      this.updateCountdown();

      this.timer = setInterval(() => {

        this.updateCountdown();

      }, 1000);

    },

    stopCountdown() {

      clearInterval(this.timer);

      this.timer = null;

    },

    updateCountdown() {

      if (!this.data.expired_at)
        return;

      const diff =
        (Number(this.data.expired_at) * 1000) - Date.now();

      if (diff <= 0) {

        this.countdown = "Expired";

        this.setStatus("EXPIRED");

        return;

      }

      const total = Math.floor(diff / 1000);

      const hours = Math.floor(total / 3600);

      const minutes = Math.floor((total % 3600) / 60);

      const seconds = total % 60;

      const pad = v => String(v).padStart(2, "0");

      this.countdown =
        hours > 0
          ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
          : `${pad(minutes)}:${pad(seconds)}`;

    },

    //----------------------------------
    // IMAGE
    //----------------------------------

    handleImageError() {

      console.warn("QR image failed to load.");

    },

    //----------------------------------
    // FORMAT
    //----------------------------------

    formatRupiah(value) {

      return new Intl.NumberFormat("id-ID", {
        maximumFractionDigits: 0
      }).format(Number(value || 0));

    },

    formatDate(timestamp) {

      const value = Number(timestamp);

      if (!value || isNaN(value))
        return "-";

      return new Date(value * 1000).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      });

    },

    //----------------------------------
    // CLEANUP
    //----------------------------------

    cleanup() {
      this.stopPolling();
      this.stopCountdown();
      this.loading=false;
    }

  }

};
</script>

<style scoped>
.payment-container {

  background: #f4f6f9;
  min-height: 100vh;

  display: flex;
  justify-content: center;
  align-items: center;

}

.payment-card {

  width: 400px;

  background: #fff;

  border-radius: 10px;

  padding: 25px;

  box-shadow: 0 5px 20px rgba(0, 0, 0, .1);
  text-align: center;

}

h2 {

  text-align: center;
  margin-bottom: 25px;

}

.info-row {

  display: flex;
  justify-content: space-between;

  margin-bottom: 12px;

}

.title {

  font-size: 13px;
  color: #666;

}

.value {

  font-weight: bold;

}

.badge {

  padding: 5px 10px;

  border-radius: 30px;

  font-size: 12px;

}

.waiting {

  background: #fff4cc;
  color: #996c00;

}

.success {

  background: #d4edda;
  color: #155724;

}


.not-found {

  background: #f8d7da;
  color: #721c24;
  font-weight: bold;

}

.qr-section {

  margin: 25px 0;
  text-align: center;

}

.qr-image {

  width: 250px;

}

.instruction {

  text-align: center;

  color: #666;

  margin-bottom: 20px;

}

.btn {

  width: 100%;

  padding: 14px;

  background: rgb(45, 61, 120);

  color: white;

  border: none;

  border-radius: 6px;

  cursor: pointer;

  font-size: 16px;

}

.btn:disabled {

  background: #aaa;

  cursor: not-allowed;

}

.success-container {
  text-align: center;
  margin-top: 20px;
}

.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: #28a745;
  color: #fff;
  font-size: 46px;
  line-height: 80px;
  font-weight: bold;
}

.success-title {
  color: #28a745;
  margin-bottom: 10px;
}

.success-message {
  color: #666;
  line-height: 1.6;
  margin-bottom: 25px;
}

.receipt {
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 15px;
  text-align: left;
  background: #fafafa;
}

.receipt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.receipt-row:last-child {
  border-bottom: none;
}

.receipt-row span {
  color: #666;
}

.receipt-row strong {
  color: #222;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

.expired {
  background: #fdecea;
  color: #c62828;
  padding: 10px;
  border-radius: 8px;
  font-weight: bold;
}
</style>

